/**
 * Readiness. The CMS annual readiness checklist for one contract year.
 * Compliance: Overview, My items (its own focused list), Items, Owners, Setup.
 * Department users: My items.
 * House layout (Materials Review, Audit): white cards, slate, one orange,
 * no dark badges, no emoji. The open item lives in the address bar.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api, itemFromUrl, setItemInUrl } from './api.js';
import { TabBar, Button } from './ui.jsx';
import OverviewTab from './OverviewTab.jsx';
import ItemsTab from './ItemsTab.jsx';
import ItemPage from './ItemPage.jsx';
import RosterTab from './RosterTab.jsx';
import SetupTab from './SetupTab.jsx';
import MyItemsTab from './MyItemsTab.jsx';
import CorrespondenceTab from './CorrespondenceTab.jsx';
import FdrTab from './FdrTab.jsx';

const fmtUtc = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

export default function ReadinessApp({ user, initialBoot = null, initialTab = 'overview' }) {
  const [boot, setBoot] = useState(initialBoot); // initialBoot: the render harness only
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [filter, setFilter] = useState({});
  const [openId, setOpenIdState] = useState(() => itemFromUrl());
  const [people, setPeople] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const setOpenId = (id) => { setItemInUrl(id); setOpenIdState(id); };
  const refresh = () => setRefreshKey((k) => k + 1);

  const load = useCallback(async () => {
    const d = await api('/bootstrap');
    if (!d?.success) { setErr(d?.message || 'Could not load'); return; }
    setBoot(d);
    if (d.is_compliance && !people) {
      const p = await api('/people');
      if (p?.success) setPeople(p);
    }
  }, [people]);
  useEffect(() => { if (!initialBoot) load(); }, [load, refreshKey, initialBoot]);

  if (err) return <div className="p-10 text-sm text-rose-600">Readiness could not load: {err}</div>;
  if (!boot) return <div className="p-10 text-sm text-slate-400">Loading Readiness…</div>;

  const ov = boot.overview;
  const year = boot.cycle?.contract_year || boot.edition.contract_year;
  const tabs = boot.is_compliance
    ? [['overview', 'Overview', ov?.decisions?.total || 0], ['my', 'My items', boot.my_open || 0], ['items', 'Items', ov?.totals?.open || 0], ['roster', 'Owners', boot.roster.confirmed ? 0 : boot.roster.review.length], ['fdr', 'FDRs', boot.fdr_pending || 0], ['mail', 'Correspondence'], ['setup', 'Setup']]
    : [['my', 'My items']];
  const current = openId ? 'item' : (boot.is_compliance ? tab : 'my');
  const goItems = (f) => { setFilter(f || {}); setOpenId(null); setTab('items'); };

  return (
    <div className="h-full overflow-auto bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Readiness · Contract Year {year}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              The CMS readiness checklist, item by item, in plain words, with an owner and an answer for each.{boot.org_name ? ` Plan: ${boot.org_name}.` : ''}
              {boot.cycle ? (boot.cycle.internal_due_date
                ? ` Internal due date ${fmtUtc(boot.cycle.internal_due_date)}; CMS deadline ${fmtUtc(boot.cycle.due_date)}.`
                : ` Due ${fmtUtc(boot.cycle.due_date)}.`) : ''}
            </p>
          </div>
          {boot.is_compliance && !boot.cycle && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 max-w-sm">The year is not open yet. Overview walks you through the three steps: the plan profile, the owner roster, and opening the year.</div>
          )}
          {boot.is_compliance && boot.auto_opened && (
            <div className="text-xs text-slate-700 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2 max-w-md">Contract year {boot.auto_opened} is open: every CMS item is in, each topic given to a department with everyone in it. Check Setup and Owners, then Re-apply if you change anything.</div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <TabBar value={current} onChange={(v) => { setOpenId(null); setTab(v); }} tabs={openId ? [...tabs, ['item', 'Item']] : tabs} />
          {openId && <Button kind="secondary" onClick={() => { setOpenId(null); refresh(); }}>Back to list</Button>}
        </div>

        <div className="mt-6">
          {openId ? (
            <ItemPage key={openId} id={openId} boot={boot} people={people} onChanged={refresh} onClose={() => { setOpenId(null); refresh(); }} />
          ) : !boot.is_compliance ? (
            <MyItemsTab boot={boot} onOpen={setOpenId} refreshKey={refreshKey} />
          ) : tab === 'overview' ? (
            <OverviewTab boot={boot} onRefresh={refresh} onGoItems={goItems} onGoTab={setTab} />
          ) : tab === 'my' ? (
            <MyItemsTab boot={boot} onOpen={setOpenId} refreshKey={refreshKey} />
          ) : tab === 'items' ? (
            <ItemsTab boot={boot} people={people} filter={filter} onFilter={setFilter} onOpen={setOpenId} refreshKey={refreshKey} />
          ) : tab === 'roster' ? (
            <RosterTab boot={boot} people={people} onChanged={refresh} />
          ) : tab === 'fdr' ? (
            <FdrTab boot={boot} onChanged={refresh} />
          ) : tab === 'mail' ? (
            <CorrespondenceTab boot={boot} onOpenItem={setOpenId} />
          ) : (
            <SetupTab boot={boot} onSaved={refresh} />
          )}
        </div>
      </div>
    </div>
  );
}
